/**
 * ewayBillDataSource.ts — WatermelonDB → e-way bill candidate bridge.
 *
 * Loads SALE invoices whose value exceeds the e-way bill threshold for a date
 * range and maps the store (consignor), customer (consignee), and line items
 * into the EWB shape. Transport details are NOT in the DB — the UI collects
 * them per invoice and merges them before export.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Invoice, LineItem, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Customer } from '@/db/models/Customers';
import { Product } from '@/db/models/Inventory';
import Store from '@/db/models/Store';
import Merchant from '@/db/models/Merchant';
import { EWB_THRESHOLD, type EwbInvoiceInput, type EwbItemInput, type EwbParty } from './ewayBill';

const CHUNK = 500;

async function fetchInChunks<T>(ids: string[], run: (batch: string[]) => Promise<T[]>): Promise<T[]> {
  const out: T[] = [];
  for (let i = 0; i < ids.length; i += CHUNK) {
    out.push(...(await run(ids.slice(i, i + CHUNK))));
  }
  return out;
}

/** An invoice eligible for an e-way bill, minus the transport leg. */
export interface EwbCandidate {
  invoiceId: string;
  invoiceNumber: string;
  value: number;
  customerName: string;
  base: Omit<EwbInvoiceInput, 'transport'>;
}

function toPincode(p: string | null | undefined): number | null {
  const n = parseInt((p ?? '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) ? n : null;
}

function lineToItem(li: LineItem, productName: string): EwbItemInput {
  const inter = li.igstAmount > 0;
  return {
    productName,
    hsnCode: li.hsnCode,
    quantity: li.quantity,
    unit: li.unit,
    taxableAmount: li.taxableAmount,
    cgstRate: inter ? 0 : li.gstRate / 2,
    sgstRate: inter ? 0 : li.gstRate / 2,
    igstRate: inter ? li.gstRate : 0,
    cessRate: li.cessRate,
  };
}

/** Load e-way-bill candidates (sale value > ₹50,000) for [from, to]. */
export async function loadEwayCandidates(storeId: string, from: Date, to: Date): Promise<EwbCandidate[]> {
  const store = await database.get<Store>('stores').find(storeId);
  let merchant: Merchant | null = null;
  try {
    merchant = await database.get<Merchant>('merchants').find(store.merchantId);
  } catch {
    merchant = null;
  }

  const fromParty: EwbParty = {
    gstin: store.gstinOverride || merchant?.gstin || null,
    tradeName: merchant?.businessName || store.name || 'Store',
    address1: store.address || '',
    place: store.city || '',
    pincode: toPincode(store.pincode),
    state: store.state || merchant?.state || null,
  };

  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    Q.where('type', 'SALE'),
    Q.where('created_at', Q.gte(from.getTime())),
    Q.where('created_at', Q.lte(to.getTime())),
    Q.where('total_amount', Q.gt(EWB_THRESHOLD)),
  ).fetch();

  const invoiceIds = invoices.map((i) => i.id);
  const lines = await fetchInChunks(invoiceIds, (batch) =>
    database.get<LineItem>('line_items').query(
      Q.where('invoice_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );
  const linesByInvoice = new Map<string, LineItem[]>();
  for (const li of lines) {
    const arr = linesByInvoice.get(li.invoiceId) ?? [];
    arr.push(li);
    linesByInvoice.set(li.invoiceId, arr);
  }

  // Product names (fallback when a line has no stored description).
  const prodIds = [...new Set(lines.map((l) => l.productId).filter(Boolean))];
  const products = await fetchInChunks(prodIds, (batch) =>
    database.get<Product>('products').query(Q.where('id', Q.oneOf(batch))).fetch(),
  );
  const prodNames = new Map<string, string>();
  for (const p of products) prodNames.set(p.id, p.name);

  const custIds = [...new Set(invoices.map((i) => i.customerId).filter(Boolean))] as string[];
  const customers = await fetchInChunks(custIds, (batch) =>
    database.get<Customer>('customers').query(Q.where('id', Q.oneOf(batch))).fetch(),
  );
  const custMap = new Map<string, Customer>();
  for (const c of customers) custMap.set(c.id, c);

  return invoices.map((inv) => {
    const c = inv.customerId ? custMap.get(inv.customerId) : null;
    const toParty: EwbParty = {
      gstin: c?.gstin ?? null,
      tradeName: c?.name?.trim() || 'Walk-in Customer',
      address1: c?.address || '',
      place: c?.city || '',
      pincode: toPincode(c?.pincode),
      state: c?.state || null,
    };
    const items = (linesByInvoice.get(inv.id) ?? []).map((li) =>
      lineToItem(li, li.description?.trim() || prodNames.get(li.productId) || 'Item'),
    );
    return {
      invoiceId: inv.id,
      invoiceNumber: inv.invoiceNumber,
      value: inv.totalAmount,
      customerName: toParty.tradeName,
      base: {
        docNo: inv.invoiceNumber,
        docDate: inv.createdAt,
        supplyType: 'O',
        from: fromParty,
        to: toParty,
        totalTaxable: inv.taxableAmount,
        cgst: inv.cgstAmount,
        sgst: inv.sgstAmount,
        igst: inv.igstAmount,
        cess: inv.cessAmount,
        totInvValue: inv.totalAmount,
        items,
      },
    };
  });
}
